import {
  faArrowsRotate as faArrowsRotateSolid,
  faComment as faCommentSolid,
  faNoteSticky as faNoteSolid,
} from '@fortawesome/free-solid-svg-icons';
import {
  faArrowsRotate as faArrowsRotateRegular,
  faComment as faCommentRegular,
  faNoteSticky as faNoteRegular,
} from '@fortawesome/pro-regular-svg-icons';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  LoaderFunctionArgs,
  ShouldRevalidateFunction,
  useFetcher,
  useLoaderData,
  useLocation,
  useNavigate,
  useOutlet,
} from 'react-router-dom';
import { toast } from 'react-toastify';
import { useClient } from 'urql';
import { client } from '~/client';
import { Split } from '~/components/Split';
import { TagEntityType, TagEntityTypes } from '~/components/TagCombobox';
import { formatTagsToFaceted, useTags } from '~/components/TagCombobox/tags';
import { ActivityBulkActionView } from '~/components/activity/ActivityBulkActionView';
import {
  ActivityListItem,
  ActivityListItem_Activity,
} from '~/components/activity/ActivityListItem';
import { DesktopActivityList } from '~/components/activity/DesktopActivityList';
import { MobileActivityList } from '~/components/activity/MobileActivityList';
import { parseAttributeFilters } from '~/components/layout/CalendarLayout';
import { type Filter } from '~/components/ui/FilterGroup';
import { FilterGroupSearch } from '~/components/ui/FilterGroupSearch';
import { Loading } from '~/components/ui/Loading';
import { TopBar } from '~/components/ui/TopBar';
import { AddButton } from '~/components/ui/buttons/AddButton';
import { getFragmentData, graphql } from '~/gql';
import { Activity, Tag } from '~/gql/graphql';
import { formatDate } from '~/helpers/formatDate';
import parseFilters from '~/helpers/parseFilters';
import { useLocalStorage } from '~/hooks';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { useScrollTrigger } from '~/hooks/useScrollTrigger';
import { useContacts } from './resources/contacts';
import { /* extractAttributes, */ useSites } from './resources/sites';
import { getPersistedFiltersSync } from './activityFilters';
import {
  ACTIVITY_BACKGROUND_PAGE_LIMIT as BACKGROUND_PAGE_LIMIT,
  ACTIVITY_PAGE_LIMIT as PAGE_LIMIT,
  addDateSeparators,
  buildActivityLoaderFlags,
  parseActivityPageSize,
  removeUnnecessaryData,
  serializeParamsStable,
  shouldRevalidateActivity,
} from './activityList';

// Navigation component - only show TopBar on mobile; no subnav on desktop
const ActivityNav = () => {
  const { t } = useTranslation();
  return (
    <>
      <div className='lg:hidden'>
        <TopBar title={t('nav:jobs.activity')} />
      </div>
    </>
  );
};

const ActivityListItem_AddTagsToActivity = graphql(/* GraphQL */ `
  mutation AddTagsToActivity($id: ID!, $tagIds: [ID!]!) {
    addTagsToActivity(id: $id, tagIds: $tagIds) {
      id
      tags {
        id
        entityType
        category
        name
        description
        colour
      }
    }
  }
`);

const ActivityListItem_RemoveTagsFromActivity = graphql(/* GraphQL */ `
  mutation RemoveTagsFromActivity($id: ID!, $tagIds: [ID!]!) {
    removeTagsFromActivity(id: $id, tagIds: $tagIds) {
      id
      tags {
        id
        entityType
        category
        name
        description
        colour
      }
    }
  }
`);

const SEARCH_BACKGROUND_PAGE_LIMIT = 100;

const Activity_Query = graphql(/* GraphQL */ `
  query Activity(
    $first: Int
    $after: String
    $showMessages: Boolean
    $showStatus: Boolean
    $showTaskNotes: Boolean
    $unread: String
    $flagged: String
    $site: [ID!]
    $author: [ID!]
    $dateRange: String
    $tag: [String!]
    $commentTagIds: [ID!]
    $jobTagIds: [ID!]
    $taskTagIds: [ID!]
    $query: String
    $excludeAutomation: Boolean
  ) {
    tags(entityTypes: ["Activity"]) {
      nodes {
        id
        entityType
        category
        name
        description
        colour
      }
    }
    allActivity(
      first: $first
      after: $after
      showMessages: $showMessages
      showStatus: $showStatus
      showTaskNotes: $showTaskNotes
      unread: $unread
      flagged: $flagged
      site: $site
      author: $author
      dateRange: $dateRange
      tag: $tag
      commentTagIds: $commentTagIds
      jobTagIds: $jobTagIds
      taskTagIds: $taskTagIds
      query: $query
      excludeAutomation: $excludeAutomation
    ) {
      totalCount
      totalUnreadCount
      edges {
        node {
          id
          createdAt
          ...ActivityListItem_ActivityFragment
        }
        cursor
      }
      pageInfo {
        hasNextPage
      }
    }
  }
`);

const Activities_TotalCount = graphql(`
  query Activities_TotalCount(
    $first: Int
    $after: String
    $showMessages: Boolean
    $showStatus: Boolean
    $showTaskNotes: Boolean
    $unread: String
    $flagged: String
    $site: [ID!]
    $author: [ID!]
    $dateRange: String
    $tag: [String!]
    $commentTagIds: [ID!]
    $jobTagIds: [ID!]
    $taskTagIds: [ID!]
    $query: String
    $excludeAutomation: Boolean
  ) {
    allActivity(
      first: $first
      after: $after
      showMessages: $showMessages
      showStatus: $showStatus
      showTaskNotes: $showTaskNotes
      unread: $unread
      flagged: $flagged
      site: $site
      author: $author
      dateRange: $dateRange
      tag: $tag
      commentTagIds: $commentTagIds
      jobTagIds: $jobTagIds
      taskTagIds: $taskTagIds
      query: $query
      excludeAutomation: $excludeAutomation
    ) {
      totalCount
    }
  }
`);

// Simple query to update navigation cache
const ActivityUnreadCount = graphql(`
  query ActivityUnreadCount {
    allActivity {
      totalUnreadCount
    }
  }
`);

type ActivityLoaderData = Awaited<ReturnType<typeof loader>>;
type Edges = NonNullable<NonNullable<ActivityLoaderData>['activity']>['edges'];

export async function loader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url);

  // Use 'first' from URL if provided (for background loading), otherwise default to PAGE_LIMIT
  // Validate and clamp to prevent abuse (NaN, negative, or excessively large values)
  // NOTE: Backend caps at 100 items per request - this validation ensures we don't request more
  const first = parseActivityPageSize(url.searchParams.get('first'));
  const flags = buildActivityLoaderFlags(url.searchParams);

  const variables = {
    first,
    after: url.searchParams.get('after'),
    // Only pass false if explicitly set to '0', otherwise let backend default to true
    showMessages: flags.showMessages,
    showStatus: flags.showStatus,
    showTaskNotes: flags.showTaskNotes,
    unread: url.searchParams.get('filter[unread]'),
    flagged: url.searchParams.get('filter[flagged]'),
    site: url.searchParams.getAll('filter[site]'),
    author: url.searchParams.getAll('filter[author]'),
    dateRange: url.searchParams.get('filter[dateRange]'),
    tag: url.searchParams.getAll('filter[tag]'),
    commentTagIds: url.searchParams.getAll('filter[commentTagIds]'),
    jobTagIds: url.searchParams.getAll('filter[jobTagIds]'),
    taskTagIds: url.searchParams.getAll('filter[taskTagIds]'),
    query: url.searchParams.get('query'),
    excludeAutomation: flags.excludeAutomation,
  };

  // Use 'cache-first' to enable instant navigation from cache
  // The component will refresh data via fetcher after mount if needed
  // This prevents the noticeable lag when clicking the activity button
  const { error, data } = await client
    .query(Activity_Query, variables, {
      requestPolicy: 'cache-first',
    })
    .toPromise();

  if (error) {
    throw new Response(error.message, { status: 500 });
  }

  return {
    activity: data?.allActivity,
    filters: new URLSearchParams(
      [...url.searchParams.entries()].filter(([k]) => k.startsWith('filter'))
    ).toString(),
    refresh: url.searchParams.get('page') === '0',
    tags: data?.tags,
  };
}

type Items = Array<Edges[number] | string>;

type ContextType = {
  activities: Items;
};
const ActivityListContext = createContext<ContextType | undefined>(undefined);
export function useActivityListContext() {
  const context = useContext(ActivityListContext);

  if (!context) {
    throw new Error('Must be used within ActivityListContext.Provider');
  }

  return context;
}

// Optional variant used by components that can function without the Activity context
// Returns undefined when no provider is present instead of throwing
export function useOptionalActivityListContext() {
  return useContext(ActivityListContext);
}

export default function ActivityRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const data = useLoaderData() as ActivityLoaderData;
  // Initialize loading state based on whether loader provided data
  // This prevents unnecessary loading spinner when cached data is available
  const [isLoading, setIsLoading] = useState(!data?.activity);
  const urqlClient = useClient(); // Add URQL client for cache updates
  // Raw query text from the search input
  const [query, setQuery] = useState('');
  // Debounced value used for filtering and server requests
  const [debouncedQuery, setDebouncedQuery] = useState('');
  // Lazy initialization: read from localStorage synchronously during first render
  // This prevents the flash where filters show default state before persisted values load
  const [filters, setFilters] = useState<URLSearchParams>(() => {
    const persisted = getPersistedFiltersSync();
    return new URLSearchParams(persisted || data.filters);
  });
  const localStorage = useLocalStorage();
  // Once the tenant context is available, hydrate from the tenant-scoped key explicitly
  const hasHydratedTenantFiltersRef = useRef(false);
  useEffect(() => {
    if (hasHydratedTenantFiltersRef.current) return;
    try {
      const tenantValue = localStorage.get('job.activity');
      if (tenantValue) {
        hasHydratedTenantFiltersRef.current = true;
        setFilters(new URLSearchParams(tenantValue));
      }
    } catch (error) {
      console.error(
        '[Activity Filters] Error hydrating tenant-scoped filters:',
        error
      );
    }
  }, [localStorage]);
  const [selected, setSelected] = useState<string[]>([]);
  const { isMobile } = useBreakpoint();

  // REMOVED: Duplicate initial fetch that was causing double data loading
  // The loader already fetches data before mount - this useEffect was redundant
  // and caused noticeable lag by fetching the same data twice.
  // The filter change effect (below) handles fetching when filters actually change.

  const [activity, setActivity] = useState<Items>(
    removeUnnecessaryData(data?.activity?.edges ?? [])
  );

  const fetcher = useFetcher<ActivityLoaderData>();

  // Ensure initial loader data renders content
  useEffect(() => {
    if (data?.activity) {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Virtual scroll element
  const parentRef = useRef<HTMLDivElement>(null);

  // Page info
  const [hasNextPage, setHasNextPage] = useState(
    data.activity?.pageInfo.hasNextPage
  );
  const [totals, setTotals] = useState({
    count: data.activity?.totalCount ?? 0,
    unread: data.activity?.totalUnreadCount ?? 0,
  });
  const [newActivitiesCount, setNewActivitiesCount] = useState(0);
  // Track when refresh button is clicked and waiting for new data
  const [isRefreshing, setIsRefreshing] = useState(false);
  // Flag when filters come from external hydration (e.g., storage event) to avoid redundant persistence
  const isHydratingFiltersRef = useRef(false);

  const latest = data.activity?.edges?.[0]?.node.id;

  // Lazy search index - only builds when user is actively searching
  // This avoids expensive recomputation during background loading when not searching
  const searchableIndex = useMemo(() => {
    // Skip building index if not searching - return empty map
    // The index will be built on-demand when debouncedQuery becomes truthy
    if (!debouncedQuery) {
      return new Map<string, string>();
    }

    const searchIndex = new Map<string, string>();
    const edgesOnly = activity.filter(
      (a): a is Edges[number] => typeof a !== 'string'
    );
    for (const { node } of edgesOnly) {
      const data = getFragmentData(ActivityListItem_Activity, node);
      // Concatenate all searchable fields into a single lowercase string
      const searchText = [
        data?.job?.name,
        data?.task?.name,
        data?.job?.site?.name,
        data?.createdBy?.name,
        data?.task?.notes,
        data?.comment?.body,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      searchIndex.set(data.id, searchText);
    }
    return searchIndex;
  }, [activity, debouncedQuery]);

  // Client-side search filtering using pre-computed searchable index
  // Fast: only does string.includes() on cached text, no getFragmentData calls during search
  const displayedActivity = useMemo(() => {
    if (!debouncedQuery) return activity;
    const lower = debouncedQuery.toLowerCase();
    const edgesOnly = activity.filter(
      (a): a is Edges[number] => typeof a !== 'string'
    );
    const filtered = edgesOnly.filter(({ node }) => {
      const data = getFragmentData(ActivityListItem_Activity, node);
      const searchText = searchableIndex.get(data.id) ?? '';
      return searchText.includes(lower);
    });
    return addDateSeparators(filtered);
  }, [debouncedQuery, activity, searchableIndex]);

  // Choose list to render based on whether search is active
  const listToRender = debouncedQuery ? displayedActivity : activity;

  // Track loading state for background search loading
  const [isBackgroundLoading, setIsBackgroundLoading] = useState(false);

  // Count of activities searched so far (for progress feedback)
  const searchedCount = useMemo(() => {
    return activity.filter((a) => typeof a !== 'string').length;
  }, [activity]);

  // Display count respects client-side filtering AND removeUnnecessaryData filtering
  // Must count actual activity edges, not server totals, because removeUnnecessaryData
  // filters out some items (e.g., empty status updates without notes/attachments)
  const displayCount = useMemo(() => {
    // When searching, count from the filtered displayedActivity
    if (debouncedQuery) {
      const edgesOnly = displayedActivity.filter(
        (a): a is Edges[number] => typeof a !== 'string'
      );
      return edgesOnly.length;
    }
    // When not searching, count from the activity list (which has removeUnnecessaryData applied)
    const edgesOnly = activity.filter(
      (a): a is Edges[number] => typeof a !== 'string'
    );
    return edgesOnly.length;
  }, [debouncedQuery, displayedActivity, activity]);

  // Unread count for filtered results - must count actual unread items in the list
  // Cannot use server totals because removeUnnecessaryData filters out some items
  const unreadDisplayCount = useMemo(() => {
    // Determine which list to count from based on search state
    const listToCount = debouncedQuery ? displayedActivity : activity;
    const edgesOnly = listToCount.filter(
      (a): a is Edges[number] => typeof a !== 'string'
    );
    return edgesOnly.reduce((sum, { node }) => {
      const data = getFragmentData(ActivityListItem_Activity, node);
      const isRead = data.isRead ?? false;
      return sum + (isRead ? 0 : 1);
    }, 0);
  }, [debouncedQuery, displayedActivity, activity]);

  // Debounce the raw query input to reduce re-renders and filtering work on large lists
  // Only when the user pauses typing do we apply the filter and trigger fetch
  useEffect(() => {
    const debounceDelayMs = 300;
    const timerId = setTimeout(() => {
      setDebouncedQuery(query);
    }, debounceDelayMs);
    return () => clearTimeout(timerId);
  }, [query]);

  const sites = useSites();
  const contacts = useContacts();
  // const attributeOptions = extractAttributes(sites);

  // Tagging dependencies
  const [allTags] = useTags(TagEntityTypes);
  const [activityTags, reload] = useTags(TagEntityType.Activity);
  const [isTagBoxOpen, setIsTagBoxOpen] = useState(false);
  const [selectedTagsIds, setSelectedTagsIds] = useState<string[]>([]);
  const [selectedActivity, setSelectedActivity] = useState<Activity>();

  const updateTags = useCallback(async () => {
    if (!selectedActivity) return;

    const currentTagIds = (selectedActivity.tags ?? []).map((t) => t.id);
    const toAddTags = selectedTagsIds.filter(
      (id) => !currentTagIds.includes(id)
    );
    const toRemoveTags = currentTagIds.filter(
      (id) => !selectedTagsIds.includes(id)
    );

    let latestTags = selectedActivity.tags ?? [];

    // Remove first, then add, to ensure final set matches selection
    if (toRemoveTags.length) {
      const { data, error } = await urqlClient
        .mutation(ActivityListItem_RemoveTagsFromActivity, {
          tagIds: toRemoveTags,
          id: selectedActivity.id?.toString() ?? '',
        })
        .toPromise();
      if (error) {
        toast.error(error.message.replace('[GraphQL] ', ''));
      } else if (data?.removeTagsFromActivity?.tags) {
        latestTags = data.removeTagsFromActivity.tags;
      }
    }

    if (toAddTags.length) {
      const { data, error } = await urqlClient
        .mutation(ActivityListItem_AddTagsToActivity, {
          tagIds: toAddTags,
          id: selectedActivity.id?.toString() ?? '',
        })
        .toPromise();
      if (error) {
        toast.error(error.message.replace('[GraphQL] ', ''));
      } else if (data?.addTagsToActivity?.tags) {
        latestTags = data.addTagsToActivity.tags;
      }
    }

    const finalTags =
      latestTags && latestTags.length > 0
        ? latestTags
        : activityTags.filter((t) => selectedTagsIds.includes(t.id));

    setActivity(
      activity.map((a) => {
        if (typeof a !== 'string' && a.node.id === selectedActivity.id) {
          const { node } = a;
          return { ...a, node: { ...node, tags: finalTags } };
        }
        return a;
      })
    );

    setIsTagBoxOpen(false);
  }, [selectedActivity, selectedTagsIds, activity, activityTags, urqlClient]);

  // add/remove tag helpers consolidated into updateTags

  // Memoized to prevent ActivityListItem re-renders
  const handleTagActivity = useCallback((activityItem: Activity) => {
    setSelectedActivity(activityItem);
    setSelectedTagsIds(activityItem.tags?.map((t) => t.id) || []);
    setIsTagBoxOpen(true);
  }, []);

  const activityLength = activity.length;
  const filtersKey = useMemo(() => serializeParamsStable(filters), [filters]);

  // Stable reference to localStorage setter to avoid effect re-runs
  // The useLocalStorage hook returns new function references each render
  // Defined early so it can be used by handleSearchFilterChange
  const localStorageRef = useRef(localStorage);
  localStorageRef.current = localStorage;

  const saveFilters = useCallback((value: string) => {
    return localStorageRef.current.set('job.activity', value);
  }, []);

  // Callback for infinite scroll - triggered by virtualized lists when near end
  // Pagination continues even during search to expand the pool of searchable items
  const handleLoadMore = useCallback(() => {
    if (!activityLength) return;
    if (!hasNextPage) return;
    if (fetcher.state !== 'idle') return;

    // Fail-safe to force the component to block duplicate requests
    setHasNextPage(false);

    const lastEdge = [...activity]
      .reverse()
      .find((i) => typeof i !== 'string') as Edges[number] | undefined;
    if (!lastEdge) return;

    const qs = new URLSearchParams([
      ['first', String(PAGE_LIMIT)],
      ['after', lastEdge.cursor],
      ...filters.entries(),
    ]);
    // Note: query param NOT included - search is client-side only
    fetcher.load(`/activity?${qs}`);
  }, [activityLength, hasNextPage, fetcher.state, activity, filters]);

  useEffect(() => {
    if (fetcher.data?.activity) {
      if (fetcher.data?.refresh) {
        // Start again from the top - reset background loading state
        initialBackgroundStartedRef.current = false;
        setActivity(removeUnnecessaryData(fetcher.data?.activity?.edges ?? []));
        // Note: Do NOT call setFilters here - the local filter state is the source of truth
        // and is already persisted to localStorage. Syncing from server response can cause
        // race conditions where empty/stale filters overwrite user preferences.
        // Reset refresh button state - stop spinning icon and clear count
        setIsRefreshing(false);
        setNewActivitiesCount(0);
      } else {
        // Append the next page with deduplication to prevent duplicate keys
        // This handles cases where activities may overlap between pages due to timing
        setActivity((prev) => {
          const newItems = removeUnnecessaryData(
            fetcher.data?.activity?.edges ?? []
          );
          // Get IDs of existing activities (excluding date separators)
          const existingIds = new Set(
            prev
              .filter((a): a is Edges[number] => typeof a !== 'string')
              .map((a) => a.node.id)
          );
          // Filter out duplicates from new items and re-add date separators
          const uniqueNewEdges = newItems.filter(
            (item): item is Edges[number] =>
              typeof item !== 'string' && !existingIds.has(item.node.id)
          );
          // Re-add date separators for the deduplicated items
          const withSeparators = addDateSeparators(uniqueNewEdges);
          return [...prev, ...withSeparators];
        });
      }

      setHasNextPage(fetcher.data.activity.pageInfo.hasNextPage);
      setTotals({
        count: fetcher.data.activity.totalCount,
        unread: fetcher.data.activity.totalUnreadCount,
      });
      // Always clear loading state when data arrives - regardless of refresh flag
      // This fixes the "stuck loading" issue on second visit
      setIsLoading(false);
      // Reset background loading state when data arrives
      setIsBackgroundLoading(false);
    }
  }, [fetcher.data]);

  // Background loading when searching - automatically load ALL pages to search entire dataset
  // Uses a ref to track the current search query and throttle requests
  const backgroundSearchQueryRef = useRef<string | null>(null);
  const lastBackgroundLoadTimeRef = useRef<number>(0);
  const BACKGROUND_LOAD_DELAY_MS = 500; // Delay between background requests to avoid rapid-fire
  const INITIAL_BACKGROUND_DELAY_MS = 500; // Delay after initial load before background fetching starts

  // Track if initial background loading has started (not search-related)
  const initialBackgroundStartedRef = useRef(false);

  // Automatic background loading after initial page renders (not search-related)
  // This preloads data for faster search and smoother scrolling
  useEffect(() => {
    // Only run once after initial load completes
    if (initialBackgroundStartedRef.current) return;
    // Wait for initial data to be present and not in loading state
    if (isLoading || !activity.length) return;
    // Need more pages available
    if (!hasNextPage) return;
    // Wait for fetcher to be idle
    if (fetcher.state !== 'idle') return;
    // Don't interfere with search background loading
    if (debouncedQuery) return;

    // Mark as started to prevent re-triggering
    initialBackgroundStartedRef.current = true;

    const timeoutId = setTimeout(() => {
      // Double-check conditions still valid after delay
      if (!hasNextPage || fetcher.state !== 'idle' || debouncedQuery) return;

      const lastEdge = [...activity]
        .reverse()
        .find((i) => typeof i !== 'string') as Edges[number] | undefined;

      if (lastEdge) {
        lastBackgroundLoadTimeRef.current = Date.now();
        const qs = new URLSearchParams([
          ['first', String(BACKGROUND_PAGE_LIMIT)],
          ['after', lastEdge.cursor],
          ...filters.entries(),
        ]);
        fetcher.load(`/activity?${qs}`);
      }
    }, INITIAL_BACKGROUND_DELAY_MS);

    return () => clearTimeout(timeoutId);
    // Note: activity excluded from deps intentionally - we only want this to trigger
    // once after initial load, not on every activity update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, hasNextPage, fetcher.state, debouncedQuery, filters]);

  // Continue automatic background loading until all pages are fetched (not search-related)
  // This continues fetching in the background after the first background batch
  useEffect(() => {
    // Only proceed if initial background loading has started
    if (!initialBackgroundStartedRef.current) return;
    // Don't run during search (search has its own background loading)
    if (debouncedQuery) return;
    // Need more pages available
    if (!hasNextPage) return;
    // Wait for fetcher to be idle
    if (fetcher.state !== 'idle') return;

    // Throttle: ensure minimum delay between background loads
    const now = Date.now();
    const timeSinceLastLoad = now - lastBackgroundLoadTimeRef.current;
    const delayNeeded = Math.max(
      0,
      BACKGROUND_LOAD_DELAY_MS - timeSinceLastLoad
    );

    const timeoutId = setTimeout(() => {
      // Double-check conditions still valid after delay
      if (!hasNextPage || fetcher.state !== 'idle' || debouncedQuery) return;

      lastBackgroundLoadTimeRef.current = Date.now();

      const lastEdge = [...activity]
        .reverse()
        .find((i) => typeof i !== 'string') as Edges[number] | undefined;

      if (lastEdge) {
        const qs = new URLSearchParams([
          ['first', String(BACKGROUND_PAGE_LIMIT)],
          ['after', lastEdge.cursor],
          ...filters.entries(),
        ]);
        fetcher.load(`/activity?${qs}`);
      }
    }, delayNeeded);

    return () => clearTimeout(timeoutId);
  }, [hasNextPage, fetcher.state, activity, filters, debouncedQuery]);

  // Search-specific background loading - uses larger page size for faster search coverage
  useEffect(() => {
    // Track which query triggered this effect for cleanup
    backgroundSearchQueryRef.current = debouncedQuery;

    // Only trigger background loading when searching
    if (!debouncedQuery) {
      setIsBackgroundLoading(false);
      return;
    }
    // Need more pages available
    if (!hasNextPage) {
      setIsBackgroundLoading(false);
      return;
    }
    // Don't overload - wait for fetcher to be idle
    if (fetcher.state !== 'idle') return;

    // Throttle: ensure minimum delay between background loads
    const now = Date.now();
    const timeSinceLastLoad = now - lastBackgroundLoadTimeRef.current;
    const delayNeeded = Math.max(
      0,
      BACKGROUND_LOAD_DELAY_MS - timeSinceLastLoad
    );

    const timeoutId = setTimeout(() => {
      // Double-check conditions still valid after delay
      if (!debouncedQuery || !hasNextPage || fetcher.state !== 'idle') return;
      if (backgroundSearchQueryRef.current !== debouncedQuery) return; // Query changed

      setIsBackgroundLoading(true);
      lastBackgroundLoadTimeRef.current = Date.now();

      const lastEdge = [...activity]
        .reverse()
        .find((i) => typeof i !== 'string') as Edges[number] | undefined;

      if (lastEdge) {
        const qs = new URLSearchParams([
          ['first', String(SEARCH_BACKGROUND_PAGE_LIMIT)],
          ['after', lastEdge.cursor],
          ...filters.entries(),
        ]);
        fetcher.load(`/activity?${qs}`);
      }
    }, delayNeeded);

    // Cleanup: cancel pending timeout and reset loading state
    return () => {
      clearTimeout(timeoutId);
      if (backgroundSearchQueryRef.current !== debouncedQuery) {
        setIsBackgroundLoading(false);
      }
    };
  }, [debouncedQuery, hasNextPage, fetcher.state, activity, filters]);

  // Client-side keyword search fallback computed via useMemo above

  function appendTagsValue(filtersValue: URLSearchParams) {
    const next = new URLSearchParams(filtersValue);
    const parsedFilters = parseFilters(next);
    const selectedTags = parseAttributeFilters(
      parsedFilters['filter[allTags]']
    );

    const filterNames: Array<
      'commentTagIds' | 'jobTagIds' | 'taskTagIds' | 'tag'
    > = ['commentTagIds', 'jobTagIds', 'taskTagIds', 'tag'];

    for (const filterName of filterNames) {
      const tagEntityType =
        filterName === 'jobTagIds'
          ? TagEntityType.Job
          : filterName === 'commentTagIds'
          ? TagEntityType.Comment
          : filterName === 'taskTagIds'
          ? TagEntityType.Task
          : TagEntityType.Activity;

      const idx = selectedTags.findIndex(
        (o) => o.id === tagEntityType.toLowerCase()
      );

      if (idx === -1) {
        next.delete(`filter[${filterName}]`);
        continue;
      }
      // If tags haven't loaded yet, skip appending to avoid runtime errors
      if (!allTags || allTags.length === 0) {
        continue;
      }
      const values = selectedTags[idx].value;
      // Reset existing ids for this filter before appending fresh values
      next.delete(`filter[${filterName}]`);
      for (const tagName of values) {
        const tag = allTags.find((tag) => tag.name === tagName);
        if (!tag?.id) continue;
        next.append(`filter[${filterName}]`, tag.id);
      }
    }

    return next;
  }

  function appendDateRangeValue(filtersValue: URLSearchParams) {
    const next = new URLSearchParams(filtersValue);
    const dateAfter = next.get('filter[dateRange]After');
    const dateBefore = next.get('filter[dateRange]Before');
    next.delete('filter[dateRange]');

    if (!dateAfter || !dateBefore) return next;

    // Backend expects "start_end" date-only string (no time)
    const normalizeDate = (value: string) => {
      const date = new Date(value);
      return Number.isNaN(date.getTime())
        ? null
        : value.substring(0, 10) || null;
    };

    const normalizedAfter = normalizeDate(dateAfter);
    const normalizedBefore = normalizeDate(dateBefore);

    if (!normalizedAfter || !normalizedBefore) return next;

    next.append('filter[dateRange]', `${normalizedAfter}_${normalizedBefore}`);
    return next;
  }

  const handleSearchFilterChange = useCallback(
    ({
      searchValue,
      filtersValue,
    }: {
      searchValue: string;
      filtersValue: URLSearchParams;
    }) => {
      // Update the raw text immediately for input UX and filters snapshot
      setQuery(searchValue);
      // Only update filters if the serialized value actually changed to avoid reload loops
      const nextKey = serializeParamsStable(filtersValue);
      const filtersString = filtersValue.toString();

      if (nextKey !== filtersKey) {
        setFilters(filtersValue);
        // Save to localStorage immediately when user changes filters
        // This ensures persistence even if the user navigates away quickly
        saveFilters(filtersString);
      }
    },
    [filtersKey, saveFilters]
  );

  const handleTagClick = useCallback(
    (tag: Tag) => {
      const newFilters = new URLSearchParams([...filters.entries()]);
      const type =
        tag.entityType === TagEntityType.Job
          ? 'jobTagIds'
          : tag.entityType === TagEntityType.Comment
          ? 'commentTagIds'
          : tag.entityType === TagEntityType.Task
          ? 'taskTagIds'
          : 'tag';

      const preSelectedTags = newFilters.getAll(`filter[${type}]`);

      if (!preSelectedTags.includes(tag.id)) {
        newFilters.append(
          'filter[allTags]',
          `${tag.entityType.toLocaleLowerCase()},${tag.name}`
        );

        handleSearchFilterChange({
          searchValue: query,
          filtersValue: newFilters,
        });
      }
    },
    [filters, query, handleSearchFilterChange]
  );

  // Track the initial filters loaded from localStorage to avoid saving them back unnecessarily
  const initialFiltersKeyRef = useRef<string | null>(null);
  if (initialFiltersKeyRef.current === null) {
    initialFiltersKeyRef.current = filtersKey;
  }

  // Persist filters to localStorage whenever they change from user actions
  // This is separate from the fetch effect to ensure saves happen even if fetch is skipped
  // We skip saving if filters match the initial state (already in localStorage or empty defaults)
  useEffect(() => {
    const filtersString = filters.toString();
    // Don't save on initial mount - filters came from localStorage or loader defaults
    if (filtersKey === initialFiltersKeyRef.current) {
      return;
    }
    saveFilters(filtersString);
  }, [filtersKey, filters]);

  // Trigger server fetches when filters change (NOT search query - that's client-side only)
  // Also handles initial data refresh - loader provides cached data for instant navigation,
  // then this effect fetches fresh data with the correct filters from localStorage
  const lastFetchKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const skipPersist = isHydratingFiltersRef.current;
    if (skipPersist) {
      isHydratingFiltersRef.current = false;
    }

    const fetcherParams = new URLSearchParams(filters);
    const filtersWithDateRange = appendDateRangeValue(fetcherParams);
    const filtersWithTags = appendTagsValue(filtersWithDateRange);

    const qs = new URLSearchParams(filtersWithTags);
    qs.set('first', String(PAGE_LIMIT));
    qs.set('page', '0');
    // Note: query param NOT included - server-side search causes 504 timeouts
    // Search is handled client-side on loaded items instead

    const requestKey = serializeParamsStable(qs);
    if (lastFetchKeyRef.current === requestKey) {
      return;
    }
    lastFetchKeyRef.current = requestKey;
    setIsLoading(true);
    fetcher.load(`/activity?${requestKey}`);
    // Persist filters to localStorage
    if (!skipPersist) {
      localStorage.set('job.activity', filters.toString());
    }
  }, [filtersKey]);

  // Callback for when user clicks "new updates" button - refetch and reset count
  // Shows spinning icon while loading new data
  const handleScrollToTopRefresh = useCallback(() => {
    // Set refreshing state to show spinning icon
    setIsRefreshing(true);

    const fetcherParams = new URLSearchParams(filters);
    const filtersWithDateRange = appendDateRangeValue(fetcherParams);
    const filtersWithTags = appendTagsValue(filtersWithDateRange);
    const qs = new URLSearchParams(filtersWithTags);
    qs.set('first', String(PAGE_LIMIT));
    qs.set('page', '0');
    // Intentionally omit query to avoid reloading due to keyword typing
    fetcher.load(`/activity?${serializeParamsStable(qs)}`);
  }, [filters]);

  const outlet = useOutlet();

  useEffect(() => {
    if (!outlet) setSelected([]);
  }, [outlet]);

  // Persist and sync status toggle (and all filters) across tabs/windows via localStorage
  // Note: This only fires when localStorage is modified by ANOTHER tab/window
  const filtersKeyRef = useRef(filtersKey);
  filtersKeyRef.current = filtersKey;

  useEffect(() => {
    const handler = (e: StorageEvent) => {
      // Accept tenant-scoped or global keys
      if (!e.key) return;
      if (!e.key.endsWith('::job.activity') && e.key !== 'global::job.activity')
        return;
      const newValue = (e.newValue as string) || '';
      try {
        const next = new URLSearchParams(newValue);
        // Only update if different, to avoid loops
        if (serializeParamsStable(next) !== filtersKey) {
          isHydratingFiltersRef.current = true;
          setFilters(next);
        }
      } catch {
        // ignore malformed
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [filtersKey]);

  // Polling the total number of new activities
  useEffect(() => {
    // Activity type toggles - all default to true (showing), set to false when explicitly hidden ('0')
    const showMessagesParam = filters.get('filter[showMessages]');
    const showStatusParam = filters.get('filter[showStatus]');
    const showTaskNotesParam = filters.get('filter[showTaskNotes]');
    // excludeAutomation defaults to true (hide automated activity) unless explicitly set to '1'
    const showAutomation = filters.get('filter[showAutomation]') === '1';

    const variables = {
      first: PAGE_LIMIT,
      after: filters.get('after'),
      showMessages: showMessagesParam === '0' ? false : undefined,
      showStatus: showStatusParam === '0' ? false : undefined,
      showTaskNotes: showTaskNotesParam === '0' ? false : undefined,
      unread: filters.get('filter[unread]'),
      flagged: filters.get('filter[flagged]'),
      site: filters.getAll('filter[site]'),
      author: filters.getAll('filter[author]'),
      dateRange: filters.get('filter[dateRange]'),
      tag: filters.getAll('filter[tag]'),
      commentTagIds: filters.getAll('filter[commentTagIds]'),
      jobTagIds: filters.getAll('filter[jobTagIds]'),
      taskTagIds: filters.getAll('filter[taskTagIds]'),
      query: filters.get('query'),
      excludeAutomation: !showAutomation,
    };

    const interval = setInterval(async () => {
      const { data } = await urqlClient
        .query(Activities_TotalCount, variables, {
          requestPolicy: 'cache-and-network',
        })
        .toPromise();

      const newTotal = data?.allActivity.totalCount;
      if (!newTotal) return;

      setTotals((prev) => {
        if (newTotal > prev.count) {
          const newCount = newTotal - prev.count;
          setNewActivitiesCount(newCount);
          return {
            count: newTotal,
            unread: prev.unread + newCount,
          };
        }
        return prev;
      });
      // Poll every 30 seconds instead of 5 seconds to reduce server load
      // Real-time updates could use WebSockets in the future for better UX
    }, 30000);

    return () => clearInterval(interval);
  }, [urqlClient, filters]);
  const { pathname, search } = useLocation();
  const isCreateFromActivity =
    pathname === '/activity/jobs/new' && search.search('activity') >= 0;
  const showBulkAction =
    (selected.length > 1 || (selected.length > 0 && !outlet)) &&
    !isCreateFromActivity;

  // Memoized to prevent ActivityListItem re-renders when parent state changes
  const handleSelect = useCallback(
    (id: string, isSelected: boolean, isSingle: boolean) => {
      if (isSingle) return setSelected([id]);

      setSelected((prev) =>
        isSelected ? prev.filter((item) => item !== id) : [...prev, id]
      );
    },
    []
  );

  // Memoized to prevent unnecessary re-renders
  const handleBulkCreateJobs = useCallback(() => {
    if (!selected.length) return;
    navigate(`jobs/new?activity=${selected.join(',')}`);
  }, [selected, navigate]);

  const isScrollingDown = useScrollTrigger({ enabled: true });

  // Memoized handler for when an activity is clicked (decrements unread count)
  // Uses functional state update to avoid stale closure over totals
  const handleActivityClick = useCallback(() => {
    setTotals((prev) => ({
      ...prev,
      unread: Math.max(0, prev.unread - 1),
    }));
  }, []);

  // Create a shared function to handle mark all as read optimistically
  const handleMarkAllAsReadOptimistic = useCallback(async () => {
    // Update local state immediately
    setTotals((prev) => ({ ...prev, unread: 0 }));
    setActivity((prev) =>
      prev.map((item) => {
        if (typeof item === 'string') return item;
        return {
          ...item,
          node: {
            ...item.node,
            isRead: true,
          },
        };
      })
    );

    // Immediately update the URQL cache for navigation
    try {
      await urqlClient
        .query(
          ActivityUnreadCount,
          {},
          {
            requestPolicy: 'network-only',
          }
        )
        .toPromise();
    } catch (error) {
      console.warn('Failed to update activity cache:', error);
    }
  }, [urqlClient]);

  // Memoized callback for tag box close
  const handleTagBoxClose = useCallback(() => {
    setIsTagBoxOpen(false);
    setSelectedTagsIds([]);
  }, []);

  // Memoized callback for tags change
  const handleTagsChange = useCallback(
    (tags: Tag[]) => {
      reload();
      setSelectedTagsIds(tags.map(({ id }) => id));
    },
    [reload]
  );

  // Render function for each virtual row - shared between mobile and desktop
  const renderRow = useCallback(
    ({
      virtualRow,
      measureElement,
    }: {
      virtualRow: {
        key: string | number | bigint;
        index: number;
        start: number;
        size: number;
      };
      measureElement: (el: HTMLElement | null) => void;
    }) => {
      const item = listToRender[virtualRow.index];
      // Check if this is a loader row (index beyond list bounds)
      // When searching, there's no loader row since we're filtering already-loaded items
      const isLoaderRow = !item && !debouncedQuery;
      // If item is undefined and we're searching, skip rendering (shouldn't happen but safety check)
      if (!item && debouncedQuery) return null;
      const isDateSeparatorRow = typeof item === 'string';
      const isSelected =
        (!isDateSeparatorRow &&
          selected.includes((item as Edges[number])?.node?.id)) ||
        false;
      const node =
        !isDateSeparatorRow && (item as Edges[number])?.node
          ? getFragmentData(
              ActivityListItem_Activity,
              (item as Edges[number]).node
            )
          : null;
      const showCreateJobFrom =
        !!node?.job?.id && !!(node?.comment || node?.task);

      return (
        <div
          key={String(virtualRow.key)}
          data-index={virtualRow.index}
          ref={measureElement}
          className='px-3 lg:px-4'
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            transform: `translateY(${virtualRow.start}px)`,
          }}
        >
          {isLoaderRow ? (
            hasNextPage ? (
              'Loading more...'
            ) : (
              'Nothing more to load'
            )
          ) : isDateSeparatorRow ? (
            <div className='mx-0 flex items-center gap-3 lg:mx-[10%]'>
              <div className='h-[.5px] flex-1 bg-[#dddee0]'></div>
              <span className='py-2 text-center text-sm text-grey-40'>
                {formatDate(item as string, 'PP')}
              </span>
              <div className='h-[.5px] flex-1 bg-[#dddee0]'></div>
            </div>
          ) : (
            <div className='flex min-w-0 items-center gap-2'>
              <div
                className='mb-3 hidden w-6 shrink-0 items-center justify-start overflow-hidden p-1 lg:flex'
                onClick={() =>
                  handleSelect(
                    (item as Edges[number]).node.id,
                    isSelected,
                    false
                  )
                }
              >
                {showCreateJobFrom && (
                  <input
                    type='checkbox'
                    className='form-checkbox cursor-pointer disabled:border-grey-20 disabled:bg-grey-10'
                    checked={isSelected}
                    readOnly
                  />
                )}
              </div>
              <ActivityListItem
                onSelect={(id, isSingle) =>
                  handleSelect(id, isSelected, isSingle)
                }
                activity={(item as Edges[number]).node}
                onTag={handleTagActivity}
                onTagClick={handleTagClick}
                onClick={handleActivityClick}
                showCreateJobFrom={showCreateJobFrom}
              />
            </div>
          )}
        </div>
      );
    },
    [
      debouncedQuery,
      listToRender,
      selected,
      hasNextPage,
      handleSelect,
      handleTagActivity,
      handleTagClick,
      handleActivityClick,
    ]
  );

  // Memoize context value to prevent unnecessary re-renders of all context consumers
  const contextValue = useMemo(() => ({ activities: activity }), [activity]);
  const isStatusVisible = filters.get('filter[showStatus]') !== '0';
  const automationFilters: Filter[] = useMemo(
    () =>
      isStatusVisible
        ? [
            'divider',
            // Automation toggle - toggle ON to SHOW automated activity (default is OFF = hidden)
            {
              name: 'filter[showAutomation]',
              type: 'toggle',
              label: 'Automation Activity',
              toggle: '1',
            },
          ]
        : [],
    [isStatusVisible]
  );

  return (
    <ActivityListContext.Provider value={contextValue}>
      {/** Count to display respects client-side keyword filtering */}
      {/** Memoized to avoid recalculation during virtual scroll */}
      {(() => {
        return null;
      })()}
      <Split
        outletHidden={showBulkAction}
        hasBackground={true}
        nav={<ActivityNav />}
        height='lg:h-screen-minus-compact-nav'
        filters={
          <div className='flex w-full items-center'>
            <FilterGroupSearch
              filters={[
                // Quick filters with counts - Unread and Flagged as pill toggles
                {
                  name: 'filter[unread]',
                  type: 'countToggle',
                  label: 'Unread',
                  toggle: '1',
                  count: totals.unread,
                },
                {
                  name: 'filter[flagged]',
                  type: 'countToggle',
                  label: 'Flagged',
                  toggle: '1',
                  // Note: totalFlaggedCount not available from backend yet - count badge will show when added
                },
                'divider',
                // Activity type toggles - all ON by default, toggle OFF to hide
                // Regular (outline) icon when inactive, solid (filled) icon when active
                {
                  name: 'filter[showMessages]',
                  type: 'iconToggle',
                  label: t('message_plural'),
                  toggle: '1',
                  icon: faCommentRegular,
                  iconActive: faCommentSolid,
                  invertedLogic: true, // Empty = showing (default), '0' = hidden
                },
                {
                  name: 'filter[showStatus]',
                  type: 'iconToggle',
                  label: 'Status',
                  toggle: '1',
                  icon: faArrowsRotateRegular,
                  iconActive: faArrowsRotateSolid,
                  invertedLogic: true,
                },
                {
                  name: 'filter[showTaskNotes]',
                  type: 'iconToggle',
                  label: 'Notes',
                  toggle: '1',
                  icon: faNoteRegular,
                  iconActive: faNoteSolid,
                  invertedLogic: true,
                },
                'divider',
                {
                  name: 'filter[allTags]',
                  label: t('tag_plural'),
                  // ! This is not what faceted means
                  options: formatTagsToFaceted(allTags),
                  type: 'facetedSelect',
                },
                {
                  name: 'filter[site]',
                  type: 'select',
                  label: t('site'),
                  options: sites.map((site) => ({
                    value: site.id,
                    label: site.name,
                  })),
                },
                {
                  name: 'filter[author]',
                  type: 'select',
                  label: 'Author',
                  options: contacts.map((contact) => ({
                    value: contact.id,
                    label: contact.name,
                  })),
                },
                {
                  name: 'filter[dateRange]',
                  type: 'date',
                  label: 'Date Range',
                },
                ...automationFilters,
              ]}
              value={{ searchValue: query, filtersValue: filters }}
              onChange={handleSearchFilterChange}
              placement='portal'
            />
          </div>
        }
        main={
          isLoading ? (
            <div className='flex h-48 items-center justify-center'>
              <Loading spinner />
            </div>
          ) : (
            <>
              {isMobile ? (
                <MobileActivityList
                  listToRender={listToRender}
                  hasNextPage={hasNextPage ?? false}
                  displayCount={displayCount}
                  unreadDisplayCount={unreadDisplayCount}
                  totalsUnread={totals.unread}
                  latest={latest}
                  newActivitiesCount={newActivitiesCount}
                  debouncedQuery={debouncedQuery}
                  isBackgroundLoading={isBackgroundLoading}
                  searchedCount={searchedCount}
                  totalToSearch={totals.count}
                  activityTags={activityTags}
                  selectedTagsIds={selectedTagsIds}
                  isTagBoxOpen={isTagBoxOpen}
                  onTagBoxClose={handleTagBoxClose}
                  onTagsChange={handleTagsChange}
                  onTagSubmit={updateTags}
                  onScrollToTop={handleScrollToTopRefresh}
                  onMarkAllAsReadOptimistic={handleMarkAllAsReadOptimistic}
                  onLoadMore={handleLoadMore}
                  renderRow={renderRow}
                  isRefreshing={isRefreshing}
                />
              ) : (
                <DesktopActivityList
                  listToRender={listToRender}
                  hasNextPage={hasNextPage ?? false}
                  displayCount={displayCount}
                  unreadDisplayCount={unreadDisplayCount}
                  totalsUnread={totals.unread}
                  latest={latest}
                  newActivitiesCount={newActivitiesCount}
                  debouncedQuery={debouncedQuery}
                  isBackgroundLoading={isBackgroundLoading}
                  searchedCount={searchedCount}
                  totalToSearch={totals.count}
                  activityTags={activityTags}
                  selectedTagsIds={selectedTagsIds}
                  isTagBoxOpen={isTagBoxOpen}
                  onTagBoxClose={handleTagBoxClose}
                  onTagsChange={handleTagsChange}
                  onTagSubmit={updateTags}
                  onScrollToTop={handleScrollToTopRefresh}
                  onMarkAllAsReadOptimistic={handleMarkAllAsReadOptimistic}
                  onLoadMore={handleLoadMore}
                  renderRow={renderRow}
                  isScrollingDown={isScrollingDown}
                  parentRef={parentRef}
                  isRefreshing={isRefreshing}
                />
              )}

              {showBulkAction ? (
                <ActivityBulkActionView
                  selected={selected}
                  onClose={() => setSelected([])}
                  onCreateJob={() => handleBulkCreateJobs()}
                />
              ) : null}
            </>
          )
        }
      />
      <AddButton label='Job' to='jobs/new' />
    </ActivityListContext.Provider>
  );
}

export const shouldRevalidate: ShouldRevalidateFunction = (args) => {
  return shouldRevalidateActivity({
    defaultShouldRevalidate: args.defaultShouldRevalidate,
    formAction: args.formAction,
    currentPathname: args.currentUrl.pathname,
    nextPathname: args.nextUrl.pathname,
  });
};
